/**
 * TransferScreen — send tokens
 *
 * Any token with a balance > 0 can be sent (not just A/EOS/USDT):
 * the selector is driven by the account's actual balances, with logos.
 *
 * Other features:
 * - Real-time USD preview for amount being sent
 * - Paste button in recipient field
 * - Available balance shows USD equivalent
 * - Balance validation before broadcasting
 */

import { useEffect, useMemo, useState } from 'react'
import type { Session } from '@wharfkit/session'
import { getExplorerUrl, transfer, validateTransfer, formatUsd, sortTokensForDisplay } from '@properwallet/chain-eos'
import type { TokenBalance } from '@properwallet/chain-eos'
import type { TranslationKey } from '@properwallet/i18n'
import { useWalletStore } from '../store/wallet.store'
import { useAppStore } from '../store/app.store'
import { useTranslation } from '../hooks/useTranslation'
import BottomNav from '../components/BottomNav'
import TokenPicker, { tokenKeyOf } from '../components/TokenPicker'

export default function TransferScreen() {
  const { t } = useTranslation()
  const {
    activeAccount, activeSession, balances, navigate, goBack, transferTokenKey,
    transferRecipient, setTransferRecipient,
  } = useWalletStore()
  const { selectedExplorer, setDiscoveryUrl } = useAppStore()

  const [recipient, setRecipient] = useState(transferRecipient ?? '')
  const [amount, setAmount] = useState('')
  const [memo, setMemo] = useState('')
  const [selectedToken, setSelectedToken] = useState<TokenBalance | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [txResult, setTxResult] = useState<{ txid: string } | null>(null)

  // Opened from an account chip — consume the pre-filled recipient once.
  useEffect(() => {
    if (!transferRecipient) return
    setRecipient(transferRecipient)
    setTransferRecipient(null)
  }, [transferRecipient, setTransferRecipient])

  /** Only tokens the account actually holds can be sent. */
  const sendableTokens = useMemo(
    () => sortTokensForDisplay(balances.filter(b => b.amountRaw > 0)),
    [balances],
  )

  // Pick the pre-selected token (from the wallet card) or the largest holding.
  useEffect(() => {
    if (sendableTokens.length === 0) {
      setSelectedToken(null)
      return
    }
    setSelectedToken(prev => {
      if (prev) {
        // Keep the current choice, refreshed with the latest balance data.
        const fresh = sendableTokens.find(tok => tokenKeyOf(tok) === tokenKeyOf(prev))
        if (fresh) return fresh
      }
      const preselected = transferTokenKey
        ? sendableTokens.find(tok => tokenKeyOf(tok) === transferTokenKey)
        : undefined
      return preselected ?? sendableTokens.find(tok => tok.symbol === 'A') ?? sendableTokens[0]
    })
  }, [sendableTokens, transferTokenKey])

  const symbol = selectedToken?.symbol ?? ''
  const maxAmount = selectedToken?.amount ?? '0'
  const pricePerToken =
    selectedToken?.usdValue !== undefined && selectedToken.amountRaw > 0
      ? selectedToken.usdValue / selectedToken.amountRaw
      : undefined

  const amountNum = parseFloat(amount) || 0
  const amountUsd = pricePerToken !== undefined ? amountNum * pricePerToken : undefined
  const availUsd = selectedToken?.usdValue

  const handlePasteRecipient = async () => {
    try {
      const text = await navigator.clipboard.readText()
      const cleaned = text.trim().toLowerCase().replace(/[^a-z1-5.]/g, '')
      setRecipient(cleaned)
    } catch {
      // Clipboard read not supported (requires user gesture / permission)
    }
  }

  const handleSend = async () => {
    if (!activeAccount || !activeSession) {
      setError(t('governance.walletLocked'))
      return
    }
    if (!selectedToken) {
      setError(t('transfer.errors.noToken'))
      return
    }

    setError('')
    const params = {
      from: activeAccount.name,
      to: recipient,
      amount: parseFloat(amount),
      symbol: selectedToken.symbol,
      contract: selectedToken.contract,
      precision: selectedToken.precision,
      memo,
    }

    const validation = validateTransfer(params, selectedToken.amountRaw)
    if (validation) {
      const errKey = `transfer.errors.${validation}` as TranslationKey
      setError(t(errKey))
      return
    }

    setLoading(true)
    try {
      const result = await transfer(activeSession as Session, params)
      setTxResult({ txid: result.txid })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Transfer failed')
    } finally {
      setLoading(false)
    }
  }

  // ── Success view ─────────────────────────────────────────────────────────────
  if (txResult) {
    return (
      <div className="flex flex-col h-full" style={{ background: '#000000' }}>
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6">
          <div className="glow-bg flex flex-col items-center gap-4">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{
                background: 'rgba(74,222,128,0.12)',
                border: '1px solid rgba(74,222,128,0.25)',
                boxShadow: '0 0 24px rgba(74,222,128,0.25)',
              }}
            >
              <span className="text-4xl text-success">✓</span>
            </div>
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-xl font-bold text-white">{t('transfer.success')}</h2>
            <p className="text-sm text-gray-400">
              {amount} {symbol} → {recipient}
            </p>
            {amountUsd !== undefined && amountUsd > 0 && (
              <p className="text-xs text-green-400">{formatUsd(amountUsd)}</p>
            )}
          </div>
          <div className="card w-full space-y-2">
            <p className="text-xs text-gray-500">{t('transfer.txId')}</p>
            <p className="text-xs font-mono text-gray-300 break-all">{txResult.txid}</p>
            <button
              type="button"
              onClick={() => {
                setDiscoveryUrl(getExplorerUrl(selectedExplorer, 'tx', txResult.txid))
                navigate('discovery')
              }}
              className="text-xs text-brand-light underline"
            >
              {t('transfer.viewOnExplorer')} →
            </button>
          </div>
          <button className="btn-primary w-full" onClick={() => navigate('wallet')}>
            {t('common.done')}
          </button>
        </div>
        <BottomNav />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full" style={{ background: '#000000' }}>
      {/* Header */}
      <div
        className="pt-safe px-4 py-3 flex items-center gap-3 flex-shrink-0"
        style={{ background: '#0c100c', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
      >
        <button type="button" onClick={goBack} className="text-gray-400 text-sm">
          ← {t('common.back')}
        </button>
        <h2 className="text-white font-semibold flex-1 text-center">{t('transfer.title')}</h2>
        <div className="w-12" />
      </div>

      <div className="scroll-area px-4 py-4 space-y-4">
        {/* Token selector — any token with balance > 0 */}
        <div className="space-y-1.5">
          <p className="section-label">{t('transfer.token')}</p>
          <TokenPicker
            tokens={sendableTokens}
            selected={selectedToken}
            onSelect={tok => {
              setSelectedToken(tok)
              setAmount('')
              setError('')
            }}
          />
          {selectedToken && (
            <p className="text-xs text-gray-500 text-right">
              {t('transfer.availableBalance')}:{' '}
              <span className="text-white">
                {parseFloat(maxAmount).toFixed(selectedToken.precision)} {symbol}
              </span>
              {availUsd !== undefined && availUsd > 0 && (
                <span className="text-green-400 ml-1">({formatUsd(availUsd)})</span>
              )}
            </p>
          )}
        </div>

        {/* Recipient */}
        <div className="space-y-1.5">
          <p className="section-label">{t('transfer.recipient')}</p>
          <div className="relative">
            <input
              type="text"
              value={recipient}
              onChange={e => setRecipient(e.target.value.toLowerCase().replace(/[^a-z1-5.]/g, ''))}
              placeholder={t('transfer.recipientPlaceholder')}
              className="input-field pr-16"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={13}
            />
            <button
              type="button"
              onClick={handlePasteRecipient}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-brand-light font-medium px-1 py-0.5 rounded hover:bg-brand/10 transition-colors"
              title="Paste"
            >
              📋
            </button>
          </div>
        </div>

        {/* Amount */}
        <div className="space-y-1.5">
          <p className="section-label">{t('transfer.amount')}</p>
          <div className="relative">
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder={t('transfer.amountPlaceholder')}
              className="input-field pr-16"
              inputMode="decimal"
              step="0.0001"
              min="0"
            />
            <button
              onClick={() => setAmount(maxAmount)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-brand-light font-medium"
            >
              {t('transfer.sendAll')}
            </button>
          </div>
          {/* Real-time USD preview */}
          {amountUsd !== undefined && amountNum > 0 && (
            <p className="text-xs text-green-400 text-right">≈ {formatUsd(amountUsd)}</p>
          )}
        </div>

        {/* Memo */}
        <div className="space-y-1.5">
          <p className="section-label">{t('transfer.memo')}</p>
          <input
            type="text"
            value={memo}
            onChange={e => setMemo(e.target.value)}
            placeholder={t('transfer.memoPlaceholder')}
            className="input-field"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="banner-danger text-danger text-sm">{error}</div>
        )}

        {/* No session warning */}
        {!activeSession && (
          <div
            className="rounded-xl px-3 py-2 text-sm text-warning"
            style={{
              background: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.22)',
            }}
          >
            ⚠️ {t('governance.walletLocked')}
          </div>
        )}

        {/* Submit */}
        <button
          className="btn-primary w-full py-3"
          onClick={handleSend}
          disabled={
            loading || !selectedToken || !recipient || !amount || parseFloat(amount) <= 0
          }
        >
          {loading ? t('transfer.sending') : t('transfer.confirmSend')}
        </button>
      </div>

      <BottomNav />
    </div>
  )
}
